import { useEffect, useMemo } from 'react';
import { Calculator, Minus, Plus, Wallet } from 'lucide-react';
import { calcNetSalary, netSalaryColor } from './mockDriverErpData';

const inputClass =
  'w-full rounded-lg border border-logi-grid bg-white px-3 py-2 font-mono text-sm text-logi-text outline-none transition focus:border-violet-500/50 focus:ring-1 focus:ring-logi-accent/30';

const labelClass = 'mb-1 block text-[10px] font-semibold uppercase tracking-wider text-logi-text0';

function SalaryRow({ label, amount, type }) {
  const isPenalty = type === 'penalty';
  const isNet = type === 'net';
  const isEmpty = amount === 0 && type === 'earning';

  if (isEmpty) return null;

  return (
    <div
      className={`flex items-center justify-between py-1.5 text-sm ${
        isNet ? 'mt-1 border-t border-logi-grid pt-2' : ''
      }`}
    >
      <span
        className={`flex items-center gap-1.5 ${
          isNet
            ? 'font-bold text-logi-text'
            : isPenalty
              ? 'text-red-700/90'
              : 'text-logi-muted'
        }`}
      >
        {type === 'earning' && <Plus className="h-3 w-3 text-emerald-500/70" />}
        {isPenalty && <Minus className="h-3 w-3 text-red-700/70" />}
        {isNet && <Calculator className="h-3.5 w-3.5 text-logi-accent" />}
        {label}
      </span>
      <span
        className={`font-mono font-semibold tabular-nums ${
          isNet
            ? 'text-lg'
            : isPenalty
              ? 'text-red-700'
              : 'text-emerald-700'
        }`}
      >
        {isPenalty ? '−' : isNet ? '' : '+'}
        {amount.toLocaleString()} AED
      </span>
    </div>
  );
}

function NumberField({ label, value, onChange, min = 0, max = 20000, step = 50 }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        className={inputClass}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-violet-500"
      />
    </div>
  );
}

export default function SalaryStructureTab({
  ledger,
  selectedDriverId,
  onDriverChange,
  basicSalary,
  onBasicSalaryChange,
  tripIncentives,
  onTripIncentivesChange,
  overtime,
  onOvertimeChange,
  penalties,
  onPenaltiesChange,
  onUpdateLedger,
}) {
  const selectedRecord = ledger.find((r) => r.driverId === selectedDriverId);

  const liveRecord = useMemo(
    () =>
      selectedRecord
        ? {
            ...selectedRecord,
            basicSalary,
            tripIncentives,
            overtime,
            penalties,
          }
        : null,
    [selectedRecord, basicSalary, tripIncentives, overtime, penalties],
  );

  const netSalary = liveRecord ? calcNetSalary(liveRecord) : 0;
  const netColor = liveRecord ? netSalaryColor(netSalary, basicSalary) : 'text-logi-accent';

  const fleetNet = ledger.reduce((sum, r) => {
    if (r.driverId === selectedDriverId && liveRecord) {
      return sum + calcNetSalary(liveRecord);
    }
    return sum + calcNetSalary(r);
  }, 0);

  useEffect(() => {
    if (!selectedDriverId || !onUpdateLedger) return;
    onUpdateLedger(selectedDriverId, {
      basicSalary,
      tripIncentives,
      overtime,
      penalties,
    });
  }, [basicSalary, tripIncentives, overtime, penalties, selectedDriverId, onUpdateLedger]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-logi-grid bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-emerald-700" strokeWidth={1.75} />
          <div>
            <p className="text-sm font-bold text-logi-text">Live Salary Calculator</p>
            <p className="text-xs text-logi-text0">{ledger.length} drivers · June 2026 cycle</p>
          </div>
        </div>
        <div className="rounded-lg bg-emerald-50 px-4 py-2 ring-1 ring-emerald-500/20">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
            Fleet Payroll Total
          </p>
          <p className="font-mono text-lg font-bold text-emerald-700">
            {fleetNet.toLocaleString()} AED
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-logi-grid bg-white p-4">
          <label className={labelClass}>Select Driver</label>
          <select
            className={inputClass}
            value={selectedDriverId}
            onChange={(e) => onDriverChange(e.target.value)}
          >
            {ledger.map((r) => (
              <option key={r.driverId} value={r.driverId}>
                {r.driverName}
              </option>
            ))}
          </select>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumberField
              label="Basic Salary (AED)"
              value={basicSalary}
              onChange={onBasicSalaryChange}
              max={15000}
              step={100}
            />
            <NumberField
              label="Trip Incentives (AED)"
              value={tripIncentives}
              onChange={onTripIncentivesChange}
              max={5000}
              step={50}
            />
            <NumberField
              label="Overtime (AED)"
              value={overtime}
              onChange={onOvertimeChange}
              max={3000}
              step={25}
            />
            <NumberField
              label="Penalties (AED)"
              value={penalties}
              onChange={onPenaltiesChange}
              max={2000}
              step={25}
            />
          </div>
        </div>

        {liveRecord && (
          <div className="rounded-xl border border-logi-accent/30 bg-logi-accent/10 p-4 ring-1 ring-violet-500/15">
            <div className="mb-3 flex items-center justify-between border-b border-logi-grid/80 pb-3">
              <div>
                <p className="font-semibold text-logi-text">{liveRecord.driverName}</p>
                <p className="font-mono text-[10px] text-logi-text0">{liveRecord.driverId}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">
                  Net Salary (Live)
                </p>
                <p className={`font-mono text-2xl font-bold ${netColor}`}>
                  {netSalary.toLocaleString()} <span className="text-xs">AED</span>
                </p>
              </div>
            </div>

            <div className="space-y-0.5">
              <SalaryRow label="Basic Salary" amount={basicSalary} type="earning" />
              <SalaryRow label="Trip Incentives" amount={tripIncentives} type="earning" />
              <SalaryRow label="Overtime" amount={overtime} type="earning" />
              <SalaryRow
                label="Night Allowance"
                amount={liveRecord.nightAllowance}
                type="earning"
              />
              <SalaryRow
                label="Fuel Saving Bonus"
                amount={liveRecord.fuelSavingBonus}
                type="earning"
              />
              <SalaryRow
                label="Safe Driving Bonus"
                amount={liveRecord.safeDrivingBonus}
                type="earning"
              />
              {penalties > 0 && (
                <SalaryRow label="Penalties" amount={penalties} type="penalty" />
              )}
              <SalaryRow label="Net Salary" amount={netSalary} type="net" />
            </div>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-logi-grid bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead>
              <tr className="border-b border-logi-grid/80 bg-white text-[10px] uppercase tracking-wider text-logi-text0">
                <th className="px-4 py-2 font-semibold">Driver</th>
                <th className="px-4 py-2 font-semibold text-emerald-700/80">Basic</th>
                <th className="px-4 py-2 font-semibold text-emerald-700/80">Trip Inc.</th>
                <th className="px-4 py-2 font-semibold text-emerald-700/80">OT</th>
                <th className="px-4 py-2 font-semibold text-emerald-700/80">Night</th>
                <th className="px-4 py-2 font-semibold text-emerald-700/80">Fuel Bonus</th>
                <th className="px-4 py-2 font-semibold text-emerald-700/80">Safe Drv</th>
                <th className="px-4 py-2 font-semibold text-red-700/80">Penalties</th>
                <th className="px-4 py-2 font-semibold text-logi-accent/80">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-logi-grid/30">
              {ledger.map((r) => {
                const net = calcNetSalary(r);
                const color = netSalaryColor(net, r.basicSalary);
                return (
                  <tr
                    key={r.driverId}
                    className={`font-mono text-xs hover:bg-logi-silver ${
                      r.driverId === selectedDriverId ? 'bg-logi-accent/10' : ''
                    }`}
                  >
                    <td className="px-4 py-2.5 font-sans font-semibold text-logi-text">
                      {r.driverName}
                    </td>
                    <td className="px-4 py-2.5 text-emerald-700/90">{r.basicSalary.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-emerald-700/90">{r.tripIncentives.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-emerald-700/90">{r.overtime.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-emerald-700/90">{r.nightAllowance.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-emerald-700/90">{r.fuelSavingBonus.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-emerald-700/90">{r.safeDrivingBonus.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-red-700">
                      {r.penalties > 0 ? `−${r.penalties.toLocaleString()}` : '—'}
                    </td>
                    <td className={`px-4 py-2.5 text-base font-bold ${color}`}>
                      {net.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
